import { BrandMark } from "./BrandMark";
import { ButtonLink } from "./Button";
import { Container } from "./Container";
import { VisuallyHidden } from "./VisuallyHidden";
import { HOME_SECTIONS, ROUTE_PATHS } from "../lib/routes";
import { sellerSubmitUrl } from "../lib/seller";
import styles from "./SiteHeader.module.css";

/**
 * The masthead.
 *
 * The wordmark is preceded by the Dealbid brandmark in the accent, hidden from the
 * accessibility tree because the wordmark beside it already names the link. `WEB-029` put it
 * there in place of the `01` tile: canon decision `D-013` withdrew that tile because the
 * product's identity and the board's first position were the same object, so the masthead
 * read as a rank somebody had won. `01` now means rank 01, on the board, and nowhere else.
 *
 * Beside the identity sits a plain statement of when the board resets, in the figure face:
 * canon `AGENTS.md` § 11 forbids manufactured urgency, so this is the fact and not a
 * countdown.
 *
 * `WEB-030` is this bar's third arrangement, and the reason it is not a canon change is that
 * none of the three ever was: `WEB-008` cut the canvas's six links to four and spent the
 * width on an action, `WEB-025` restored the drawing exactly, and the operator — having now
 * read both — has asked for four destinations and an action again. All three are revision 1
 * of `contracts/design/web-homepage/README.md`; none reached `docs/DECISIONS.md`, because a
 * masthead's link inventory decides no ranking, disclosure, badge or auction rule.
 *
 * What canon settled is untouched through all three. The action reads `Submit a deal`, never
 * `Bid your deal`: canon § 14 puts bidding behind a founder's Google session that a public
 * page has no way to hold, so the bar can name submission and cannot name bidding.
 * `test/components/chrome.test.tsx` keeps the refused phrase asserted-absent through a bar
 * that has now had a button, lost it, and got one back.
 *
 * And `WEB-006`'s "demote, do not delete" holds in this direction too. `How it works` and
 * `What is Dealbid?` are linked from the footer on every page of the site; `#subscribe` *is*
 * the footer's own card rather than a destination a link has to reach; and the board heading
 * keeps its `New here? What is Dealbid ↓` aside, which is the first-time visitor's way in and
 * the reason an explainer link was ever in this bar. Nothing is reachable only from a bar
 * that no longer offers it.
 *
 * `WEB-036` takes the row's *width* cap off. `WEB-030` had put the row in the page container
 * so the wordmark lined up with the board's heading; the operator, having read that page, has
 * asked for the drawing's relationship instead — a bar that runs the full width over a
 * narrower column. The row keeps the container's gutter and drops its cap, so nothing below
 * `--page-max` moves and the bar goes on widening above it. The rule and the trade against the
 * canvas's own 28px are in `SiteHeader.module.css` and in departure 10.
 *
 * The theme control does **not** come back with the links. `contracts/DESIGN-TOKENS.md`
 * § Theme makes a persisted override first-class and the canvas draws one theme per
 * artboard, so it cannot draw a control anywhere; departure 1 stands and it stays in the
 * footer (`ThemeToggle`).
 *
 * Below `78rem` the nav collapses into the canvas's `Menu`. It is a `details`/`summary`
 * disclosure rather than a scripted overlay: it is keyboard-operable and screen-reader
 * announced with no focus trap to get wrong, and it has no animation to exempt from
 * `prefers-reduced-motion`. Closed it is one line, which is what keeps the board's first
 * row above the fold on a phone.
 *
 * `WEB-047` changes what that line *holds*, and nothing else about it. The disclosure stays
 * a `details`/`summary` for every reason above; what changes is that its summary is a
 * hamburger rather than the word `Menu`, and that the width the word was spending now
 * carries the submit action itself, as a square accent control to the left of it. A founder
 * on a phone is the reader most likely to be a founder at all, and until now the one action
 * a public page can offer them was two taps away behind a closed menu.
 *
 * Both are icon-only and both carry their name in text through `VisuallyHidden`, which is
 * what `contracts/DESIGN-TOKENS.md` § Accessibility's "meaningful labels" requires; the
 * glyphs themselves are `aria-hidden`. The panel keeps its own labelled `Submit a deal`
 * button, so the action exists in visible words for anyone who opens the menu.
 *
 * The icons are not a contract violation. `contracts/DESIGN-TOKENS.md`'s "No glyph icons"
 * rule sits under § Shared component patterns → Badges and governs the five badges, which
 * carry their meaning by shape and by their own names in words. It has never been a
 * site-wide ban: the board row draws a chevron and every masthead wears an SVG brandmark.
 */

/**
 * The menu's mark. Three rules, `aria-hidden` — the summary's own text carries the name, and
 * `details`/`summary` already announces the expanded state, so a glyph that also tried to
 * would announce the control twice.
 */
function MenuGlyph() {
  return (
    <svg className={styles.glyph} viewBox="0 0 18 14" aria-hidden="true" focusable="false">
      <path
        d="M1 1h16M1 7h16M1 13h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * The submit action's mark: a plus, for adding a deal that is not there yet. Deliberately
 * not an arrow — the row's chevron already means "this opens", and an arrow here would read
 * as navigation within the page rather than as submission.
 */
function SubmitGlyph() {
  return (
    <svg className={styles.glyph} viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        d="M9 2v14M2 9h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/*
 * The four destinations (`WEB-030`).
 *
 * They are the four things a *buyer* arrived for — the board, the mentions outside it,
 * tomorrow's auction, and the lists buyers themselves decide. What left is the explanatory
 * furniture: `How it works` and `Subscribe`, which are a table of contents for a page the
 * reader is already looking at, and the accent-underlined `What is Dealbid?`. The bar spends
 * their width on the one thing a buyer's page could not otherwise offer a founder.
 *
 * Every entry is a full path plus fragment (`HOME_SECTIONS`), so the bar works from the
 * static pages too, where a bare `#weekly` would name nothing on the page it is on.
 */
const NAV_LINKS = [
  { href: HOME_SECTIONS.today, label: "Top 10" },
  { href: HOME_SECTIONS.honorable, label: "Honorable Mentions" },
  { href: HOME_SECTIONS.tomorrow, label: "Tomorrow’s Auction" },
  { href: HOME_SECTIONS.byClicks, label: "Top by Clicks" },
] as const;

/**
 * Whether the masthead offers the Honorable Mentions link (`WEB-024`).
 *
 * `HonorableMentions` renders nothing on a day with no mentions, which is every day of cold
 * start — and the link went on pointing at `#honorable` regardless, so it scrolled nowhere.
 * `WEB-022`'s cold-start canvas drops the nav item along with the section.
 *
 * The masthead cannot answer this for itself. It is site-wide, it holds no board data, and
 * the static pages render it while deliberately fetching nothing (`WEB-018`) — so the fact
 * is passed in by whoever *does* know, and the header stays a dumb renderer of it.
 *
 * `App` decides. The rule is that the item disappears only where the site has actually
 * looked and found nothing, never merely because this page did not look:
 *
 * - the homepage, board loaded, mentions present → shown;
 * - the homepage, board loaded, none → **hidden**, the case this exists for;
 * - the homepage, board still loading → hidden, so a cold-start load does not show the item
 *   and then take it away;
 * - any other route → shown, because from there the link is a navigation to the homepage and
 *   this page has no business retracting a site-wide destination it never checked.
 */
function NavLinks({ honorableMentions }: { honorableMentions: boolean }) {
  const links = NAV_LINKS.filter(
    (link) => honorableMentions || link.href !== HOME_SECTIONS.honorable,
  );

  return (
    <>
      {links.map((link) => (
        <a key={link.href} className={styles.navLink} href={link.href}>
          {link.label}
        </a>
      ))}
      {/*
        The action (`WEB-030`). An anchor styled as a button, so it keeps middle-click, copy
        link and history behaviour, and it leaves for the seller app through the same
        `sellerSubmitUrl()` the footer and the cold-start card use — which is what carries a
        founder's invite code across the hop (canon § 5).

        It renders in the phone menu as well as the wide bar. A founder on a phone is the
        reader most likely to be a founder at all, and an action that exists only above
        78rem is an action half the visitors never see.
      */}
      <ButtonLink className={styles.action} href={sellerSubmitUrl()}>
        Submit a deal
      </ButtonLink>
    </>
  );
}

export function SiteHeader({ honorableMentions = true }: { honorableMentions?: boolean }) {
  return (
    <header className={styles.header}>
      <a className={styles.skipLink} href="#main">
        Skip to content
      </a>
      <Container className={styles.inner}>
        <a className={styles.wordmark} href={ROUTE_PATHS.home}>
          <BrandMark className={styles.mark} />
          Dealbid
        </a>

        {/* Canon § 8: the boundary is UTC, and the masthead states it rather than counting
            down to it. A countdown here would be pressure on every page of the site. */}
        <p className={`${styles.reset} figure`}>Board resets 00:00 UTC</p>

        <nav className={styles.nav} aria-label="Main">
          <div className={styles.wideNav}>
            <NavLinks honorableMentions={honorableMentions} />
          </div>

          {/*
            The phone bar (`WEB-047`): the action, then the menu. The action is to the left
            because it is the thing being offered and the menu is how the rest is reached —
            and because the menu is the control a thumb finds at the edge of the screen.

            It leaves through the same `sellerSubmitUrl()` the wide bar, the footer and the
            cold-start card use, which is what carries a founder's invite code across the
            hop (canon § 5), and it is an anchor rather than a button so it keeps
            middle-click, copy link and history behaviour.
          */}
          <div className={styles.phoneControls}>
            <ButtonLink className={styles.phoneAction} href={sellerSubmitUrl()}>
              <SubmitGlyph />
              <VisuallyHidden>Submit a deal</VisuallyHidden>
            </ButtonLink>

            <details className={styles.menu}>
              <summary className={styles.menuButton}>
                <MenuGlyph />
                <VisuallyHidden>Menu</VisuallyHidden>
              </summary>
              <div className={styles.menuPanel}>
                <NavLinks honorableMentions={honorableMentions} />
              </div>
            </details>
          </div>
        </nav>
      </Container>
    </header>
  );
}
